import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import BdrButton from "../components/BdrButton/BdrButton";
import { placeCard } from "./tourEngine";

/**
 * The running tour: shield + spotlight + step card + an always-visible Exit.
 * Portalled into #bdr-tour-layer (outside #root), which is the only part of
 * the page tourLockout leaves interactive.
 *
 * Fail open: the card sits in an error boundary that ENDS the tour on any
 * render error, and the Exit button lives outside that boundary, so there is
 * always a way out even if the card can't draw.
 */

const SPOT_PAD = 6;

class CardBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error("[tours] card crashed, ending tour:", error);
    this.props.onCrash?.();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

// Track the anchor's viewport rect every frame while it is shown. Cheap (one
// getBoundingClientRect per frame) and it follows scrolling, resizes,
// animations and layout shifts without a listener for each.
function useAnchorRect(anchorEl) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!anchorEl) {
      setRect(null);
      return undefined;
    }
    let raf = 0;
    let last = "";
    const tick = () => {
      if (!anchorEl.isConnected) {
        setRect(null);
      } else {
        const r = anchorEl.getBoundingClientRect();
        const key = `${Math.round(r.top)}|${Math.round(r.left)}|${Math.round(r.width)}|${Math.round(r.height)}`;
        if (key !== last) {
          last = key;
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right });
        }
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [anchorEl]);
  return rect;
}

function paragraphs(text) {
  return String(text || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function TourCard({ title, step, stepIndex, stepCount, phase, rect, reducedMotion, onNext, onBack }) {
  const cardRef = useRef(null);
  const [pos, setPos] = useState({ docked: false, top: -9999, left: -9999 });
  const loading = phase !== "showing";

  // Place after layout so the card's real size is known.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const size = { width: el.offsetWidth, height: el.offsetHeight };
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const spot = rect && !loading
      ? {
          top: rect.top - SPOT_PAD,
          bottom: rect.bottom + SPOT_PAD,
          left: rect.left - SPOT_PAD,
          width: rect.width + SPOT_PAD * 2,
        }
      : null;
    setPos(placeCard(spot, size, viewport, (step && step.placement) || "auto"));
  }, [rect, loading, step, stepIndex]);

  // Move focus to the card on each step so a keyboard / screen-reader user
  // lands on the new text. Deferred a frame (as BdrModal does): the provider's
  // lockout effect runs AFTER this one and records what the page had focused
  // so it can put focus back on exit — focusing synchronously here would make
  // it record the card instead.
  useEffect(() => {
    if (loading) return undefined;
    const raf = requestAnimationFrame(() => cardRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [loading, stepIndex]);

  const last = stepIndex >= stepCount - 1;
  const style = pos.docked ? undefined : { top: pos.top, left: pos.left };

  return (
    <div
      ref={cardRef}
      className={`bdr-tour-card${pos.docked ? " bdr-tour-card--docked" : ""}`}
      style={style}
      tabIndex={-1}
    >
      {title && <div className="bdr-tour-card-eyebrow">{title}</div>}
      {loading ? (
        <p className="bdr-tour-card-body" aria-live="polite">
          One moment…
        </p>
      ) : (
        <>
          {step.title && <h3 className="bdr-tour-card-title">{step.title}</h3>}
          <div className="bdr-tour-card-body" aria-live="polite">
            {paragraphs(step.body).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          {step.media && step.media.src && (
            <div className="bdr-tour-card-media">
              {step.media.type === "video" ? (
                // Reduced motion: no autoplay / loop — the controls still play it.
                <video
                  src={step.media.src}
                  autoPlay={!reducedMotion}
                  loop={!reducedMotion}
                  muted
                  playsInline
                  controls
                />
              ) : (
                <img src={step.media.src} alt={step.media.alt || ""} />
              )}
            </div>
          )}
        </>
      )}
      <div className="bdr-tour-card-footer">
        <span className="bdr-tour-card-count">
          {stepIndex + 1} of {stepCount}
        </span>
        <div className="bdr-tour-card-actions">
          {stepIndex > 0 && (
            <BdrButton size="sm" onClick={onBack} disabled={loading}>
              Back
            </BdrButton>
          )}
          <BdrButton size="sm" variant="action" onClick={onNext} disabled={loading}>
            {last ? "Done" : "Next"}
          </BdrButton>
        </div>
      </div>
    </div>
  );
}

const TourOverlay = ({
  layer,
  title,
  step,
  stepIndex,
  stepCount,
  phase,
  anchorEl,
  reducedMotion = false,
  onNext,
  onBack,
  onExit,
  onCrash,
}) => {
  const rect = useAnchorRect(phase === "showing" ? anchorEl : null);
  if (!layer) return null;

  const spotStyle = rect
    ? {
        top: rect.top - SPOT_PAD,
        left: rect.left - SPOT_PAD,
        width: rect.width + SPOT_PAD * 2,
        height: rect.height + SPOT_PAD * 2,
      }
    : null;

  // The dialog is the WHOLE layer (card + Exit), so aria-modal hides the
  // inert page, not the Exit button.
  return createPortal(
    <div
      className="bdr-tour-root"
      role="dialog"
      aria-modal="true"
      aria-label={title ? `Walkthrough: ${title}` : "Walkthrough"}
    >
      {/* Catches every pointer event so nothing under the tour reacts. The
          spotlight hole is only visual — the highlighted element is shielded
          too: look, don't touch. */}
      <div
        className="bdr-tour-shield"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
        aria-hidden="true"
      />
      {spotStyle ? (
        <div className="bdr-tour-spotlight" style={spotStyle} aria-hidden="true" />
      ) : (
        <div className="bdr-tour-scrim" aria-hidden="true" />
      )}
      <CardBoundary onCrash={onCrash}>
        <TourCard
          title={title}
          step={step || {}}
          stepIndex={stepIndex}
          stepCount={stepCount}
          phase={phase}
          rect={rect}
          reducedMotion={reducedMotion}
          onNext={onNext}
          onBack={onBack}
        />
      </CardBoundary>
      <div className="bdr-tour-exit">
        <BdrButton size="sm" onClick={onExit}>
          Exit walkthrough
        </BdrButton>
      </div>
    </div>,
    layer
  );
};

export default TourOverlay;
